import { useSettingsStore } from "../state/useSettingsStore";

interface SettingsPanelProps {
  onClose: () => void;
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
}) {
  return (
    <div style={{ marginBottom: "1rem" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "0.25rem",
          fontSize: "0.9rem",
        }}
      >
        <span>{label}</span>
        <span style={{ opacity: 0.6 }}>{format ? format(value) : value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: "100%", accentColor: "#7ec8e3" }}
      />
    </div>
  );
}

export function SettingsPanel({ onClose }: SettingsPanelProps) {
  const settings = useSettingsStore();

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0, 0, 0, 0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
    >
      <div
        className="theme-panel"
        style={{
          width: "min(90%, 480px)",
          maxHeight: "80vh",
          overflowY: "auto",
          padding: "2rem",
        }}
      >
        <h2 style={{ margin: "0 0 1.5rem", fontSize: "1.3rem" }}>Settings</h2>

        <h3 style={{ margin: "0 0 0.75rem", fontSize: "1rem", opacity: 0.7 }}>
          Text
        </h3>
        <Slider
          label="Text Speed"
          value={settings.textSpeed}
          min={10}
          max={200}
          step={5}
          onChange={(v) => settings.set("textSpeed", v)}
          format={(v) => `${v} cps`}
        />
        <Slider
          label="Auto-Advance Delay"
          value={settings.autoDelay}
          min={0.5}
          max={10}
          step={0.5}
          onChange={(v) => settings.set("autoDelay", v)}
          format={(v) => `${v}s`}
        />

        <h3
          style={{
            margin: "1rem 0 0.75rem",
            fontSize: "1rem",
            opacity: 0.7,
          }}
        >
          Audio
        </h3>
        <Slider
          label="Master Volume"
          value={settings.masterVolume}
          min={0}
          max={1}
          step={0.05}
          onChange={(v) => settings.set("masterVolume", v)}
          format={(v) => `${Math.round(v * 100)}%`}
        />
        <Slider
          label="BGM"
          value={settings.bgmVolume}
          min={0}
          max={1}
          step={0.05}
          onChange={(v) => settings.set("bgmVolume", v)}
          format={(v) => `${Math.round(v * 100)}%`}
        />
        <Slider
          label="SFX"
          value={settings.sfxVolume}
          min={0}
          max={1}
          step={0.05}
          onChange={(v) => settings.set("sfxVolume", v)}
          format={(v) => `${Math.round(v * 100)}%`}
        />
        <Slider
          label="Voice"
          value={settings.voiceVolume}
          min={0}
          max={1}
          step={0.05}
          onChange={(v) => settings.set("voiceVolume", v)}
          format={(v) => `${Math.round(v * 100)}%`}
        />

        <h3
          style={{
            margin: "1rem 0 0.75rem",
            fontSize: "1rem",
            opacity: 0.7,
          }}
        >
          Appearance
        </h3>
        <div style={{ marginBottom: "1rem" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: "0.9rem",
              cursor: "pointer",
            }}
          >
            <span>UI Theme:</span>
            <select
              value={settings.theme}
              onChange={(e) =>
                settings.set(
                  "theme",
                  e.target.value as "default" | "glass" | "neon" | "parchment",
                )
              }
              style={{
                background: "#222",
                color: "#eee",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "4px",
                padding: "0.25rem 0.5rem",
              }}
            >
              <option value="default">Default</option>
              <option value="glass">Glassmorphism</option>
              <option value="neon">Neon Terminal</option>
              <option value="parchment">Warm Parchment</option>
            </select>
          </label>
        </div>

        <h3
          style={{
            margin: "1rem 0 0.75rem",
            fontSize: "1rem",
            opacity: 0.7,
          }}
        >
          Choices
        </h3>
        <div style={{ marginBottom: "1rem" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: "0.9rem",
              cursor: "pointer",
            }}
          >
            <span>Failed-condition choices:</span>
            <select
              value={settings.failedChoiceDisplay}
              onChange={(e) =>
                settings.set(
                  "failedChoiceDisplay",
                  e.target.value as "hide" | "disabled",
                )
              }
              style={{
                background: "#222",
                color: "#eee",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "4px",
                padding: "0.25rem 0.5rem",
              }}
            >
              <option value="hide">Hide</option>
              <option value="disabled">Show (disabled)</option>
            </select>
          </label>
        </div>

        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            marginTop: "1.5rem",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={() => settings.reset()}
            className="theme-button"
            style={{
              padding: "0.5rem 1.25rem",
              fontSize: "0.9rem",
            }}
          >
            Reset Defaults
          </button>
          <button
            onClick={onClose}
            className="theme-button"
            style={{
              padding: "0.5rem 1.25rem",
              fontSize: "0.9rem",
              fontWeight: "bold",
              background: "var(--theme-accent, #7ec8e3)",
              color: "#111",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
